import React, { memo } from 'react';

export type LaneLineOffset = { topX: number; bottomX: number };

export type LaneLinesProps = {
	topY: number;
	bottomY: number;
	centerX: number;
	// Backward-compatible props for legacy 4-lane rendering
	innerTopOffset?: number;
	innerBottomOffset?: number;
	// New: explicit top/bottom X pairs for each separator line
	offsets?: LaneLineOffset[];
	strokeWidth?: number;
	topWidth?: number;
	bottomWidth?: number;
};

/**
 * LaneLines
 *
 * Renders tapered quad paths for the separator lines that divide the lane into lanes.
 * Supports either explicit offsets (preferred) or the legacy 3-line mode using
 * center + innerTopOffset/innerBottomOffset.
 */
export const LaneLines = memo(function LaneLines(props: LaneLinesProps) {
	const {
		topY,
		bottomY,
		centerX,
		innerTopOffset,
		innerBottomOffset,
		offsets,
		strokeWidth = 3, // kept for backward compatibility if needed
		topWidth = 3,
		bottomWidth = 30,
	} = props;

	// Helper to build a tapered quad path for a line centered at (topX,topY) to (bottomX,bottomY)
	const quadPath = (topX: number, bottomX: number) => {
		const tHalf = (topWidth || strokeWidth) / 2;
		const bHalf = (bottomWidth || strokeWidth) / 2;
		return [
			`M ${topX - tHalf},${topY}`,
			`L ${topX + tHalf},${topY}`,
			`L ${bottomX + bHalf},${bottomY}`,
			`L ${bottomX - bHalf},${bottomY}`,
			'Z',
		].join(' ');
	};

	return (
		<>
			{Array.isArray(offsets) && offsets.length > 0 ? (
				offsets.map((o, idx) => (
					<path key={idx} d={quadPath(o.topX, o.bottomX)} className="lane-chooser__lane-line" />
				))
			) : (
				<>
					<path d={quadPath(centerX, centerX)} className="lane-chooser__lane-line" />
					{typeof innerTopOffset === 'number' && typeof innerBottomOffset === 'number' && (
						<>
							<path
								d={quadPath(centerX - innerTopOffset, centerX - innerBottomOffset)}
								className="lane-chooser__lane-line"
							/>
							<path
								d={quadPath(centerX + innerTopOffset, centerX + innerBottomOffset)}
								className="lane-chooser__lane-line"
							/>
						</>
					)}
				</>
			)}
		</>
	);
});

export default LaneLines;
